import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./admin.module.css";
import {
  useGetUserDetailsQuery,
  useUpdateUserMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { colors } from "../../theme/colors";

/**
 * Displays read-only details for a single admin user.
 *
 * Notes:
 * - Skips fetching when the route param id is missing.
 * - TODO: Confirm whether deactivation fields should be shown for all roles.
 */
const AdminDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { source?: string; userType?: string } | null;
  const isUserDetailsRoute = location.pathname.startsWith("/users/");
  const shouldUseUserEdit = state?.source === "USER_DETAILS" || isUserDetailsRoute;

  useEffect(() => {
    if (!id) {
      navigate(-1);
    }
  }, [id, navigate]);

  // Skip fetching when the route param is missing to avoid a bad request.
  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const { data: adminDetails, isLoading } = useGetUserDetailsQuery(id ?? "", {
    skip: !id,
  });
  const [updateUser] = useUpdateUserMutation();
  const displayEmail = (adminDetails)?.mail || "";
  const displayPhone = (adminDetails)?.phone || "";
  const adminDisplayName = `${adminDetails?.surname?.toUpperCase() ?? ""}, ${adminDetails?.given_name ?? ""} ${adminDetails?.prefix ?? ""}`.trim();

  const handleActivate = async () => {
    if (!adminDetails?.id) {
      return;
    }
    try {
      setIsActivating(true);
      await updateUser({
        id: adminDetails.id,
        is_active: true,
        deactivate_reason: "",
      }).unwrap();
      setActivateModalVisible(false);
    } catch (error: any) {
      setActivateModalVisible(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsActivating(false);
    }
  };

  if (!id) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "0px 20px" }}>
            <Typography className={style.adminDetailHeading}>
              Admin Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Full Name</Typography>
                <Typography className={style.value}>
                  {`${adminDetails?.surname?.toUpperCase() ?? ""}, ${adminDetails?.given_name ?? ""
                    } ${adminDetails?.prefix ?? ""}`}
                </Typography>
              </Box>
              {adminDetails?.title && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Title</Typography>
                  <Typography className={style.value}>
                    {adminDetails?.title}
                  </Typography>
                </Box>
              )}
              <Typography className={style.adminDetailHeading}>
                Contact Details
              </Typography>
              {displayPhone && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Phone Number</Typography>
                  <Typography className={style.value}>
                    {displayPhone}
                  </Typography>
                </Box>
              )}
              {displayEmail && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Email Address</Typography>
                  <Typography className={style.value}>
                    {displayEmail}
                  </Typography>
                </Box>
              )}

              {adminDetails?.deactivate_reason && (
                <Box>
                  <Typography className={style.adminDetailHeading}>
                    Deactivation Details
                  </Typography>
                  <Box>
                    <Box className={style.detailsRow}>
                      <Typography className={style.title}>
                        Reason for deactivation
                      </Typography>
                      <Typography className={style.value}>
                        {adminDetails?.deactivate_reason}
                      </Typography>
                    </Box>
                    <Box className={style.detailsRow}>
                      <Typography className={style.title}>
                        Date of Deactivation
                      </Typography>
                      <Typography className={style.value}>
                        {adminDetails?.deactivated_at
                          ? dayjs(adminDetails?.deactivated_at).format(
                            "MMMM DD YYYY, hh:mm:ss A"
                          )
                          : "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {message && (
                <Box mt={2}>
                  <LabelBox
                    boxProps={{
                      height: 50,
                      display: "flex",
                      alignItems: "center",
                    }}
                    data={message}
                    onClose={() => setMessage(null)}
                  />
                </Box>
              )}
              <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: "transparent" },
                  }}
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  label={"Close"}
                />
                {adminDetails?.is_active ? (
                  <Button
                    sx={{
                      width: "200px",
                      height: "55px",
                      ":hover": { backgroundColor: colors.primary },
                    }}
                    variant="contained"
                    label={"Edit"}
                    onClick={() => {
                      if (shouldUseUserEdit) {
                        navigate(`/users/edit/${adminDetails?.id}`, {
                          state: { userType: "ADMIN" },
                        });
                        return;
                      }
                      navigate(`/admins/edit/${adminDetails?.id}`);
                    }}
                  />
                ) : (
                  <Button
                    sx={{
                      width: "200px",
                      height: "55px",
                      ":hover": { backgroundColor: colors.primary },
                    }}
                    variant="contained"
                    label={"Activate"}
                    onClick={() => setActivateModalVisible(true)}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {activateModalVisible && (
        <ConfirmationDialog
          visible={activateModalVisible}
          message={`Are you sure you want to activate "${adminDisplayName}"?`}
          onClose={() => setActivateModalVisible(false)}
          onConfirm={handleActivate}
          loading={isActivating}
        />
      )}
    </Box>
  );
};

export default AdminDetails;
